import { Controller } from "@hotwired/stimulus"

// Governs the SCA draft form's pricing lifecycle from the workflow wrapper,
// which contains the claim rail and (once priced) the Plan Price column:
//
//   - While an explicit (re)price is in flight, the service-line fieldset is
//     disabled so rows can't change mid-quote.
//   - Before a draft exists, the claim rail's "Add codes to update the plan
//     price" button (priceButton target) starts disabled and enables only
//     while at least one service-line HCPCS/CPT code is nonblank.
//   - The claim rail's sticky "Reprice now" button starts disabled (the
//     current quote already reflects the claim) and enables as soon as anything
//     on the claim changes — typing in a field, changing claim type, or
//     adding/removing a service-line/diagnosis row. A successful reprice
//     disables it again until the next change; a failed reprice leaves it
//     enabled so the operator can correct and retry.
//   - "Activate agreement" submits the previously persisted quote token, so a
//     dirty claim also disables activation until a successful reprice brings
//     the quote back in sync with what the operator sees. (The activation
//     panel is not rendered in the current slice; the target stays supported.)
//
// Dirty tracking is generation-based: edits made while a reprice request is in
// flight were not part of that request's payload, so a successful response
// only clears the dirty state when no mutation happened after submit-start.
//
// All handlers filter to the draft form (#sca_draft_form) because the wrapper
// may also contain other forms, whose submissions must not lock the fieldset
// or reset the dirty state.
export default class extends Controller {
  static targets = ["fieldset", "repriceButton", "activateButton", "priceButton", "planPriceSkeleton", "phoneScriptColumn", "negotiationBranch"]

  connect() {
    this.dirtyGeneration = 0
    this.submittedGeneration = 0
    // A restored page (bfcache/Turbo snapshot) can come back with code inputs
    // already filled, so the gate is computed from the DOM, not assumed.
    this.syncPriceButton()
  }

  lock(event) {
    if (!this.draftFormEvent(event)) return
    if (this.hasFieldsetTarget) this.fieldsetTarget.disabled = true
    this.showPricingSkeleton()
    // The first successful price replaces the whole workflow grid so the form
    // can switch to its persisted requote URL and the center column can enter.
    // Prevent edits after Turbo has serialized that initial request; otherwise
    // a slow quote could let the operator type into fields that the grid swap
    // would necessarily replace. Requotes remain intentionally interactive
    // outside the service-line fieldset because their streams are targeted.
    if (this.hasPriceButtonTarget) {
      this.initialCreateForm = event.target
      this.initialCreateForm.inert = true
      this.initialCreateForm.setAttribute?.("aria-busy", "true")
    }
    // The request now in flight serializes the claim as it exists at this
    // moment; anything dirty so far is included in it.
    this.submittedGeneration = this.dirtyGeneration
  }

  unlock(event) {
    if (!this.draftFormEvent(event)) return
    const initialCreateSucceeded = Boolean(event.detail?.success && this.initialCreateForm)

    // On a successful first price, keep the serialized form inert until Turbo
    // removes it with the full-grid replacement. turbo:submit-end can fire
    // before stream rendering finishes; unlocking here would create a brief
    // window where edits appear accepted and are then discarded.
    if (!initialCreateSucceeded && this.hasFieldsetTarget) this.fieldsetTarget.disabled = false
    if (this.initialCreateForm && !initialCreateSucceeded) {
      this.initialCreateForm.inert = false
      this.initialCreateForm.removeAttribute?.("aria-busy")
    }
    this.initialCreateForm = null
    if (!event.detail?.success) {
      this.restorePricingDisplay()
      return
    }

    this.clearPricingDisplaySnapshot()

    if (this.dirtyGeneration === this.submittedGeneration) {
      this.markPriced()
    } else {
      // The operator kept editing while the reprice was in flight: the fresh
      // quote does not include those edits, so the claim is still dirty.
      // Re-apply after the turbo streams from this response have rendered,
      // since they replace quote-dependent regions with in-sync state.
      requestAnimationFrame(() => this.applyDirtyState())
    }
  }

  // Pricing still runs synchronously, but the visual transition does not need
  // to wait for the response. On the first price this converts the centered
  // claim-only layout to the responsive multi-column grid and inserts the
  // Plan Price skeleton plus phone-script rail immediately. On
  // a requote it swaps only the existing Plan Price panel for the same
  // skeleton. The Turbo response replaces the matching #sca_plan_price_step;
  // a failed request restores the exact prior DOM and layout.
  showPricingSkeleton() {
    // Negotiation intentionally has no #sca_plan_price_step. Its requote
    // streams refresh Benchmarks and activation state in place, so absence of
    // Plan Price must not be mistaken for the initial, unpriced layout.
    if (this.hasNegotiationBranchTarget || !this.hasPlanPriceSkeletonTarget) return

    const grid = this.element.querySelector("#sca_workflow_grid")
    const claimColumn = grid?.querySelector("[data-testid='provider-access-case-left-column']")
    const skeleton = this.planPriceSkeletonTarget.content.firstElementChild?.cloneNode(true)
    if (!grid || !claimColumn || !skeleton) return

    const currentPlanPrice = grid.querySelector("#sca_plan_price_step")
    if (currentPlanPrice) {
      // A duplicated submit-start must not replace our saved real panel with
      // the in-flight skeleton. Keep the first snapshot for failure recovery.
      if (currentPlanPrice.getAttribute?.("data-testid") === "sca-plan-price-skeleton") return

      this.previousPlanPriceStep = currentPlanPrice
      currentPlanPrice.replaceWith(skeleton)
      return
    }

    this.previousGridClassName = grid.className
    this.previousClaimColumnClassName = claimColumn.className
    grid.className = "grid gap-6 lg:grid-cols-2 lg:items-start min-[1440px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,22rem)]"
    claimColumn.classList.add("sca-claim-column-enter")

    const centerColumn = document.createElement("div")
    centerColumn.className = "min-w-0 lg:py-4 sca-center-column-enter"
    centerColumn.dataset.testid = "provider-access-case-center-column"
    centerColumn.append(skeleton)
    grid.append(centerColumn)
    this.insertedCenterColumn = centerColumn

    // The phone-script rail enters with the skeleton so the successful grid
    // replacement doesn't produce a second layout jump. Step 1's script is
    // static, so the template holds the real column, not a placeholder.
    if (this.hasPhoneScriptColumnTarget) {
      const scriptColumn = this.phoneScriptColumnTarget.content.firstElementChild?.cloneNode(true)
      if (scriptColumn) {
        grid.append(scriptColumn)
        this.insertedScriptColumn = scriptColumn
      }
    }
  }

  restorePricingDisplay() {
    if (!this.previousPlanPriceStep && !this.insertedCenterColumn) {
      this.clearPricingDisplaySnapshot()
      return
    }

    const currentPlanPrice = this.element.querySelector("#sca_plan_price_step")
    if (this.previousPlanPriceStep) {
      if (currentPlanPrice) {
        currentPlanPrice.replaceWith(this.previousPlanPriceStep)
      } else {
        this.element
          .querySelector("[data-testid='provider-access-case-center-column']")
          ?.append(this.previousPlanPriceStep)
      }
    }

    if (this.insertedCenterColumn) {
      const grid = this.element.querySelector("#sca_workflow_grid")
      const claimColumn = grid?.querySelector("[data-testid='provider-access-case-left-column']")
      this.insertedCenterColumn.remove()
      if (this.insertedScriptColumn) this.insertedScriptColumn.remove()
      if (grid && this.previousGridClassName !== undefined) grid.className = this.previousGridClassName
      if (claimColumn && this.previousClaimColumnClassName !== undefined) {
        claimColumn.className = this.previousClaimColumnClassName
      }
    }

    this.clearPricingDisplaySnapshot()
  }

  clearPricingDisplaySnapshot() {
    this.previousPlanPriceStep = null
    this.insertedCenterColumn = null
    this.insertedScriptColumn = null
    this.previousGridClassName = undefined
    this.previousClaimColumnClassName = undefined
  }

  markDirty(event) {
    if (!event.target.closest("#sca_draft_form")) return
    this.recordDirty()
  }

  // Row add/remove buttons mutate the claim without firing input/change
  // events. This handler is bound with :capture so it runs before
  // dynamic-rows#remove detaches the row — after detachment,
  // closest("#sca_draft_form") can no longer see the form.
  markDirtyFromClick(event) {
    const actionEl = event.target.closest("[data-action]")
    if (!actionEl || !actionEl.closest("#sca_draft_form")) return
    if (/dynamic-rows#(add|remove)/.test(actionEl.dataset.action || "")) this.recordDirty()
  }

  recordDirty() {
    this.dirtyGeneration += 1
    this.applyDirtyState()
  }

  applyDirtyState() {
    if (this.hasRepriceButtonTarget) this.repriceButtonTarget.disabled = false
    if (this.hasActivateButtonTarget) this.activateButtonTarget.disabled = true
    this.schedulePriceButtonSync()
  }

  // Row removal marks the claim dirty in the capture phase — before
  // dynamic-rows detaches the row — so the code census must wait a frame for
  // the DOM to settle before recounting.
  schedulePriceButtonSync() {
    if (!this.hasPriceButtonTarget) return

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => this.syncPriceButton())
    } else {
      this.syncPriceButton()
    }
  }

  syncPriceButton() {
    if (!this.hasPriceButtonTarget) return

    this.priceButtonTarget.disabled = !this.anyServiceLineCodePresent()
  }

  anyServiceLineCodePresent() {
    const codeInputs = this.element.querySelectorAll(
      "#sca_draft_form input[name='single_case_agreement[service_lines][][code]']"
    )
    return Array.from(codeInputs).some((input) => input.value.trim().length > 0)
  }

  // The claim and the persisted quote are in sync. Reprice goes idle; the
  // activate button's disabled state is server-rendered truth (it was just
  // stream-replaced by the requote response), so it is left untouched.
  markPriced() {
    if (this.hasRepriceButtonTarget) this.repriceButtonTarget.disabled = true
  }

  draftFormEvent(event) {
    return event.target?.id === "sca_draft_form"
  }
}
